<template>
	<UserPageWrapper :accountSettings="true">
		<Title>{{ $t("seo.pages.change-password.title") }}</Title>
		<Meta name="description" :content="$t('seo.pages.change-password.description')" />
		<Link rel="canonical" :href="`https://${globalConfig.host}${$route.fullPath}`" />
		<Meta property="og:url" :content="`https://${globalConfig.host}${$route.fullPath}`" />
		<Meta property="og:type" content="website" />
		<Meta
			property="og:locale"
			:content="`${$i18n.locale}_${$i18n.locale == 'en' ? 'US' : $i18n.locale.toUpperCase()}`"
		/>
		<Meta property="og:title" :content="$t('seo.pages.change-password.title')" />
		<Meta property="og:description" :content="$t('seo.pages.change-password.description')" />
		<Meta
			property="og:image"
			content="https://res.cloudinary.com/djbs/image/upload/v1669288084/content/DreamJobs/Graphics/ogimage_dreamjobs.png"
		/>
		<Meta property="og:image:type" content="image/jpeg" />
		<Meta property="og:image:width" content="1200" />
		<Meta property="og:image:height" content="628" />

		<DjBox class="bg-gray-100 md:bg-white border-0 md:border pt-5 md:pt-8 px-5 md:px-10 pb-5 md:pb-10 md:mb-4">
			<BaseHeading type="h2" class="leading-3 text-center md:text-left mb-6">
				<span class="text-2xl">{{ $t("user.changePassword.title") }}</span>
			</BaseHeading>

			<BasePasswordBox
				v-model="currentPwd"
				@keydown="clearErrors()"
				name="password"
				:label="$t('user.field.currentPassword.label')"
				:placeholder="$t('login.field.password.placeholder')"
				:error="
					currentPwdError
						? $t(`errors.${currentPwdError}`)
						: (serverFieldErrors['password_current'] && serverFieldErrors['password_current'][0]) || ''
				"
				width="100%"
				class="mb-2"
				required
			/>

			<BasePasswordBox
				v-model="pwd"
				@keydown="clearErrors()"
				name="password"
				:label="$t('user.field.newPassword.label')"
				:placeholder="$t('login.field.password.placeholder')"
				:show-checklist="true"
				:error="
					pwdError
						? $t(`errors.${pwdError}`)
						: (serverFieldErrors['password'] && serverFieldErrors['password'][0]) || ''
				"
				width="100%"
				class="mb-2"
				required
			/>

			<BasePasswordBox
				v-model="pwdConfirmation"
				@keydown="clearErrors()"
				name="password"
				:label="$t('user.field.newPasswordConfirmation.label')"
				:placeholder="$t('login.field.password.placeholder')"
				:error="
					pwdConfirmationError
						? $t(`errors.${pwdConfirmationError}`)
						: (serverFieldErrors['password'] && serverFieldErrors['password'][0]) || ''
				"
				width="100%"
				class="mb-2"
				required
			/>

			<p
				v-if="Object.keys(serverFieldErrors).length === 0 && serverErrorMsg && serverErrorMsg.length > 0"
				class="text-dj-red text-sm mt-2"
			>
				{{ serverErrorMsg }}
			</p>

			<div class="flex justify-end">
				<BaseButton @click="changePassword" class="min-w-full sm:min-w-min px-12">
					{{ $t("user.save") }}
				</BaseButton>
			</div>
		</DjBox>
	</UserPageWrapper>
</template>
<script lang="ts">
import { defineComponent } from "vue";
import { useI18n } from "vue-i18n";
import { validatePassword } from "@@/composables/helpers";

export default defineComponent({
	name: "UserChangePasswordPage",
	setup() {
		definePageMeta({
			middleware: ["auth"],
		});

		const { locale, t } = useI18n();
		const app = useNuxtApp();
		const router = useRouter();

		const region = ref("HU");
		const currentPwd = ref("");
		const pwd = ref("");
		const pwdConfirmation = ref("");

		const currentPwdError = ref("");
		const pwdError = ref("");
		const pwdConfirmationError = ref("");

		const serverFieldErrors = ref({});
		const serverErrorMsg = ref("");

		function clearErrors() {
			currentPwdError.value.length > 0 ? (currentPwdError.value = "") : null;
			pwdError.value.length > 0 ? (pwdError.value = "") : null;
			pwdConfirmationError.value.length > 0 ? (pwdConfirmationError.value = "") : null;

			Object.keys(serverFieldErrors.value).length ? (serverFieldErrors.value = {}) : null;
			serverErrorMsg.value.length > 0 ? (serverErrorMsg.value = "") : null;
		}

		function changePassword() {
			// Validations
			currentPwd.value.length === 0 ? (currentPwdError.value = "requiredPassword") : null;
			if (pwd.value.length === 0) {
				pwdError.value = "requiredPassword";
			} else if (!validatePassword(pwd.value)) {
				pwdError.value = "weakPassword";
			}
			pwdConfirmation.value.length === 0 ? (pwdConfirmationError.value = "requiredPassword") : null;
			if (pwd.value !== pwdConfirmation.value) {
				pwdError.value = "differentPassword";
				pwdConfirmationError.value = "differentPassword";
			}

			if (!currentPwdError.value && !pwdError.value && !pwdConfirmationError.value) {
				const pwdData = {
					password_current: currentPwd.value,
					password: pwd.value,
					password_confirmation: pwdConfirmation.value,
				};

				$fetch(`/api/update-profile`, {
					// API for all profile update
					method: "PUT",
					body: {
						...pwdData,
						xsrf: useAuth.getXsrfToken(),
						token: useCookie("token").value,
						operation: "update-password",
						locale: locale.value,
						region: region.value,
					},
				})
					.then(() => {
						try {
							app.$openToast({
								message: t("login.successfulPasswordReset"),
								type: "success",
								description: "",
							});
							currentPwd.value = "";
							pwd.value = "";
							pwdConfirmation.value = "";
						} catch (error) {
							console.error(error);
						}
					})
					.catch((error) => {
						if (error.response && error.response._data) {
							serverFieldErrors.value = error.response._data.data.errors || {};
						}
						if (error.response && error.response._data && error.response._data.message) {
							serverErrorMsg.value = error.statusText || error.response._data.message || "";
						}
					});
			}
		}

		onMounted(() => {
			region.value = useRegion.getCurrentRegion().code;
			const userData = useAuth.getUser();

			if (userData && userData.isRegWithSocial == true) {
				router.push(`/${locale.value}/user/account`);
			}
		});

		return {
			region,
			currentPwd,
			pwd,
			pwdConfirmation,
			currentPwdError,
			pwdError,
			pwdConfirmationError,
			serverErrorMsg,
			serverFieldErrors,
			clearErrors,
			changePassword,
		};
	},
});
</script>
